import React, { useEffect, useRef, useState } from 'react';
import { isValidJotformFormId } from '../utils/jotformEmbed';

const JOTFORM_ORIGIN = 'https://form.jotform.com';
const INITIAL_HEIGHT = 600;

/**
 * Jotform form rendered directly in an iframe (Jotform's standard embed).
 * The form reports its content height via postMessage ("setHeight:<px>:<formId>"),
 * which we use to size the iframe so it never needs an inner scrollbar on desktop or mobile.
 */
export default function NewsletterJotform({ formId, title = 'Newsletter form' }) {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(INITIAL_HEIGHT);
  const [autoSized, setAutoSized] = useState(false);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== JOTFORM_ORIGIN) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (typeof event.data !== 'string') return;

      const [action, value] = event.data.split(':');
      if (action !== 'setHeight') return;

      const next = parseInt(value, 10);
      if (Number.isFinite(next) && next > 0) {
        setHeight(next);
        setAutoSized(true);
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [formId]);

  if (!isValidJotformFormId(formId)) return null;

  return (
    <iframe
      ref={iframeRef}
      src={`${JOTFORM_ORIGIN}/${formId}`}
      title={title}
      loading="lazy"
      allow="geolocation; microphone; camera; fullscreen; payment"
      // Fall back to an inner scrollbar until the form reports its height, so nothing is ever cut off.
      scrolling={autoSized ? 'no' : 'auto'}
      style={{
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
        height: `${height}px`,
        border: 'none',
        display: 'block',
        background: 'transparent',
      }}
    />
  );
}
